import React, { useEffect, useState } from "react";
import "./AccessManagement.css";

const FEATURES = [
    { value: "broadcast", label: "Broadcast" },
    { value: "kpi", label: "KPI" },
    { value: "feedback", label: "Feedback" },
    { value: "kmanalysis", label: "KM Analysis" },
    { value: "transcript", label: "Transcript" },
    { value: "outage", label: "Outage messages" },
    { value: "broadcastreport", label: "Broadcast Report" },
    { value: "access-management", label: "Access management" },
];

const getInitials = (email) => {
    const local = email.split("@")[0] || "";
    const parts = local.split(/[._-]/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return local.slice(0, 2).toUpperCase();
};

const normalizeEntry = (entry) =>
    typeof entry === "string" ? { email: entry, status: "active" } : { status: "active", ...entry };

const AccessManagementPage = () => {
    const [canManage, setCanManage] = useState(null);
    const [feature, setFeature] = useState("outage");
    const [entries, setEntries] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [updatingEmail, setUpdatingEmail] = useState(null);

    useEffect(() => {
        checkAccess();
    }, []);

    useEffect(() => {
        if (canManage) loadEmails();
    }, [feature, canManage]);

    const checkAccess = async () => {
        try {
            const res = await fetch("/api/access/mine");
            const data = await res.json();
            setCanManage((data.features || []).includes("access-management"));
        } catch (err) {
            console.log(err);
            setCanManage(false);
        }
    };

    const loadEmails = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/access/${feature}`);
            const data = await res.json();
            setEntries((data.emails || []).map(normalizeEntry));
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    };

    const grantAccess = async () => {
        const email = newEmail.trim().toLowerCase();
        if (!email) return;
        try {
            const res = await fetch(`/api/access/${feature}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            if (res.status === 200) {
                setNewEmail("");
                loadEmails();
            } else {
                alert("Error while granting access");
            }
        } catch (err) {
            console.log(err);
        }
    };

    const updateStatus = async (email, status) => {
        const prev = entries;
        setEntries((cur) => cur.map((e) => (e.email === email ? { ...e, status } : e)));
        setUpdatingEmail(email);
        try {
            const res = await fetch(`/api/access/${feature}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, status }),
            });
            if (res.status !== 200) {
                alert("Error while updating access status");
                setEntries(prev);
            }
        } catch (err) {
            console.log(err);
            setEntries(prev);
        }
        setUpdatingEmail(null);
    };

    const deleteAccess = async (email) => {
        if (!window.confirm(`Permanently delete ${email} from this feature's access list?`)) return;
        const prev = entries;
        setEntries((cur) => cur.filter((e) => e.email !== email));
        try {
            const res = await fetch(`/api/access/${feature}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            if (res.status !== 200) {
                alert("Error while deleting access");
                setEntries(prev);
            }
        } catch (err) {
            console.log(err);
            setEntries(prev);
        }
    };

    if (canManage === null) {
        return (
            <div className="ac-root">
                <div className="ac-gate ac-gate--pending">
                    <span className="ac-gate-dot" />
                    Checking clearance…
                </div>
            </div>
        );
    }

    if (!canManage) {
        return (
            <div className="ac-root">
                <div className="ac-gate ac-gate--denied">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M8 10V7a4 4 0 1 1 8 0v3" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M9.5 14.5L14.5 19.5M14.5 14.5L9.5 19.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                    <div>
                        <p className="ac-gate-title">Access restricted</p>
                        <p className="ac-gate-sub">You dont have clearance to manage this page.</p>
                    </div>
                </div>
            </div>
        );
    }

    const currentFeature = FEATURES.find((f) => f.value === feature);
    const activeCount = entries.filter((e) => e.status === "active").length;

    return (
        <div className="ac-root">
            <header className="ac-header">
                <div>
                    <p className="ac-eyebrow">Admin portal</p>
                    <h2 className="ac-title">Access control</h2>
                </div>
                <div className="ac-status">
                    <span className="ac-status-dot" />
                    System active
                </div>
                <div className="ac-scanline" />
            </header>

            <nav className="ac-tabs" aria-label="Feature">
                {FEATURES.map((f) => (
                    <button
                        key={f.value}
                        type="button"
                        className={`ac-tab${f.value === feature ? " ac-tab--active" : ""}`}
                        onClick={() => setFeature(f.value)}
                        aria-pressed={f.value === feature}
                    >
                        {f.label}
                    </button>
                ))}
            </nav>

            <div className="ac-ticket">
                <div className="ac-ticket-main">
                    <p className="ac-ticket-label">New clearance · {currentFeature?.label}</p>
                    <input
                        type="email"
                        placeholder="Enter your email ID"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && grantAccess()}
                    />
                </div>
                <div className="ac-ticket-perf" aria-hidden="true" />
                <div className="ac-ticket-action">
                    <button className="ac-issue-btn" onClick={grantAccess} disabled={!newEmail.trim()}>
                        Grant access
                    </button>
                </div>
            </div>

            <section className="ac-panel">
                <div className="ac-panel-head">
                    <span>Clearances</span>
                    <span className="ac-count">{activeCount} active · {entries.length} total</span>
                </div>

                {loading && <div className="ac-scan-loader"><span /></div>}

                {entries.length ? (
                    <ul className="ac-badge-list">
                        {entries.map(({ email, status }) => {
                            const isActive = status === "active";
                            return (
                                <li key={email} className={`ac-badge-row${isActive ? "" : " ac-badge-row--inactive"}`}>
                                    <span className="ac-avatar">{getInitials(email)}</span>
                                    <span className="ac-email">{email}</span>

                                    <div className="ac-status-select-wrap">
                                        <span className={`ac-pill-dot${isActive ? "" : " ac-pill-dot--off"}`} />
                                        <select
                                            className="ac-status-select"
                                            value={status}
                                            disabled={updatingEmail === email}
                                            onChange={(e) => updateStatus(email, e.target.value)}
                                            aria-label={`Status for ${email}`}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>

                                    <button
                                        className="ac-delete"
                                        onClick={() => deleteAccess(email)}
                                        aria-label={`Delete access record for ${email}`}
                                        title="Delete"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                        </svg>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div className="ac-empty">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.4" />
                            <circle cx="12" cy="10.5" r="2.25" stroke="currentColor" strokeWidth="1.4" />
                            <path d="M7.5 16.5c1-2 2.7-3 4.5-3s3.5 1 4.5 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                        </svg>
                        <p>No clearances issued yet.</p>
                    </div>
                )}
            </section>
             <div style={{ height: "150px" }}></div>
        </div>
    );
};

export default AccessManagementPage;